import React, { useMemo } from "react";
import { View, Text, useWindowDimensions, ActivityIndicator, ScrollView, TouchableOpacity } from "react-native";
import { useSelector } from "react-redux";
import RenderHtml from "react-native-render-html";
import LHOverlay from "../../../components/LHOverlay";
import { TEXT } from "../../../constants/texts";
import lightStyles, {
  createChangeLogHtmlStyles,
  createHomeScreenOverlayContainerStyle,
  createHomeScreenThemeStyles,
} from "../HomeScreen.style";
import { logger } from "../../../utils/logger";
import { useTheme } from "../../../theme";

interface ChangeLogProps {
  changeLogData?: any;
}

const processHtmlContent = (htmlContent: string): string => {
  if (!htmlContent) {
    return "";
  }

  let content = htmlContent
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#58;/g, ":")
    .replace(/&#160;/g, " ");

  content = content.replace(/\\n/g, "\n");
  content = content.replace(/\\r\\n/g, "<br />").replace(/\r\n/g, "<br />");
  content = content.replace(/\\"/g, '"');

  if (!content.includes("<p>") && !content.includes("<div>")) {
    const paragraphs = content.split("\n\n").filter((paragraph) => paragraph.trim() !== "");
    if (paragraphs.length > 1) {
      content = paragraphs.map((paragraph) => `<p>${paragraph}</p>`).join("");
    } else if (content.trim() && !content.includes("<")) {
      content = `<p>${content}</p>`;
    }
  }

  return content;
};

const formatCreatedAt = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getChangeLogRecords = (data?: any) => (
  Array.isArray(data) ? data : (data?.data || [])
);

const ChangeLog: React.FC<ChangeLogProps> = ({ changeLogData }) => {
  const [showViewMoreModal, setShowViewMoreModal] = React.useState(false);
  const { width } = useWindowDimensions();
  const { colors } = useTheme();
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  const overlayContainerStyle = useMemo(
    () => createHomeScreenOverlayContainerStyle(colors),
    [colors]
  );
  const baseHtmlStyles = useMemo(() => createChangeLogHtmlStyles(colors), [colors]);
  // Measure the actual rendered width of the content area so RenderHtml gets the
  // correct value regardless of outer container padding / margins.
  const [contentWidth, setContentWidth] = React.useState(width - 48);
  const [overlayContentWidth, setOverlayContentWidth] = React.useState(width - 80);
  const { loading } = useSelector((state: any) => state.homescreen);
  const records = React.useMemo(() => getChangeLogRecords(changeLogData), [changeLogData]);
  const latestRecord = records[0];
  const latestThreeRecords = records.slice(0, 3);
  const latestMessage = latestRecord?.details || latestRecord?.messageText || TEXT.HEADERS.CHANGE_LOG_TEXT;
  const latestCreatedAt = formatCreatedAt(latestRecord?.createdDate);
  const hasMoreRecords = records.length > 1;
  const containsHtml = latestMessage.includes("<") || latestMessage.includes("&lt;");

  logger.log("ChangeLog Data:", changeLogData);

  if (loading.changeLog) {
    return (
      <View style={[lightStyles.fullWidthCard, themeStyles.cardSurface]}>
        <Text style={[lightStyles.sectionHeaderWithMargins, themeStyles.sectionHeaderWithMargins]}>{TEXT.HEADERS.CHANGE_LOG}</Text>
        <View style={lightStyles.subTextBlock}>
          <ActivityIndicator size="large" color={colors.brand.primary} />
          <Text
            style={[
              themeStyles.loadingText,
              {
              marginTop: 12,
              fontSize: 14,
              textAlign: "center",
              },
            ]}
          >
            Loading change log...
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View
      style={[lightStyles.fullWidthCard, themeStyles.cardSurface]}
      onLayout={(e) => setContentWidth(e.nativeEvent.layout.width)}
    >
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <Text style={[lightStyles.sectionHeader, themeStyles.sectionHeader, { marginBottom: 0 }]}>{TEXT.HEADERS.CHANGE_LOG}</Text>
        {latestCreatedAt && (
          <Text style={themeStyles.changeLogMetaText}>
            <Text style={themeStyles.changeLogMetaLabel}>Created At: </Text>{latestCreatedAt}
          </Text>
        )}
      </View>

      {containsHtml ? (
        <RenderHtml
          contentWidth={contentWidth}
          source={{ html: processHtmlContent(latestMessage) }}
          tagsStyles={{
            ...baseHtmlStyles,
            p: {
              ...baseHtmlStyles.p,
              fontSize: 16,
              lineHeight: 22,
              marginBottom: 8,
            },
            div: {
              ...baseHtmlStyles.div,
              fontSize: 16,
              lineHeight: 22,
              marginBottom: 8,
            },
            span: {
              ...baseHtmlStyles.span,
              fontSize: 16,
              lineHeight: 22,
            },
            br: {
              height: 8,
            },
            body: {
              lineHeight: 22,
              fontSize: 16,
              color: colors.text.primary,
            },
          }}
        />
      ) : (
        <Text style={[lightStyles.changeLogText, themeStyles.changeLogText]}>{latestRecord ? latestMessage : TEXT.COMMON.NO_DATA}</Text>
      )}

      {hasMoreRecords && (
        <TouchableOpacity style={lightStyles.showMoreButton} onPress={() => setShowViewMoreModal(true)}>
          <Text style={[lightStyles.showMoreText, themeStyles.showMoreText]}>{TEXT.COMMON.VIEW_MORE}</Text>
        </TouchableOpacity>
      )}

      <LHOverlay
        visible={showViewMoreModal}
        onDismiss={() => setShowViewMoreModal(false)}
        title={TEXT.HEADERS.CHANGE_LOG}
        containerStyle={overlayContainerStyle}
        headerPaddingHorizontal={24}
        hideFooter={true}
        size="large"
      >
        <ScrollView
          style={lightStyles.overlayContent}
          onLayout={(e) => setOverlayContentWidth(e.nativeEvent.layout.width)}
        >
          {latestThreeRecords.length > 0 ? (
            latestThreeRecords.map((record: any, index: number) => {
              const recordMessage = record.details || record.messageText || TEXT.COMMON.NO_DATA;
              const recordContainsHtml = recordMessage.includes("<") || recordMessage.includes("&lt;");
              const recordCreatedAt = formatCreatedAt(record.createdDate);

              return (
                <View key={record.id || index}>
                  <View style={{ paddingVertical: 12 }}>
                    {recordCreatedAt && (
                      <Text style={[themeStyles.changeLogMetaText, { marginBottom: 8 }] }>
                        <Text style={themeStyles.changeLogMetaLabel}>Created At: </Text>{recordCreatedAt}
                      </Text>
                    )}
                    <View>
                      {recordContainsHtml ? (
                        <RenderHtml
                          contentWidth={overlayContentWidth}
                          source={{ html: processHtmlContent(recordMessage) }}
                          tagsStyles={baseHtmlStyles}
                        />
                      ) : (
                        <Text style={[lightStyles.changeLogText, themeStyles.changeLogText]}>{recordMessage}</Text>
                      )}
                    </View>
                  </View>
                  {index < latestThreeRecords.length - 1 && <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />}
                </View>
              );
            })
          ) : (
            <View style={lightStyles.noDataContainer}>
              <Text style={[lightStyles.noDataText, themeStyles.noDataText]}>{TEXT.COMMON.NO_DATA}</Text>
            </View>
          )}
        </ScrollView>
      </LHOverlay>
    </View>
  );
};

export default ChangeLog;
